import React from 'react'
import { Form, Formik } from 'formik'
import * as yup from 'yup'
import { filter, map, path, pipe, sum } from 'ramda'
import { Button, ManagementFeeForm } from '../../../../components'
import styles from './CommissionForm.module.scss'
import { formatCurrency } from 'utils/currency'
import { $TSFixMe } from 'types/ts-migrate'
import { commissionUndertakings } from 'modules/api/portfolio/constants'
import { PortfolioRentInvoice, ManagementFeeValues } from '../types'
import RouterPromptModal from 'components/organisms/Modals/RouterPromptModal/RouterPromptModal'

type TProps = {
  redirect: (url: string) => void
  handleSubmit: (values: $TSFixMe) => void
  initialValues: {
    managementFee: ManagementFeeValues
  }
  rentAmount: number | string
  defaultResultSets: Array<Array<$TSFixMe>>
  portfolioId: string
  isDraftPortfolio: boolean
  /** Optional: a lease need not have a rent invoice template yet. */
  portfolioRentInvoice?: PortfolioRentInvoice
  isReadOnly?: boolean
}

// @ts-expect-error
const getSplitPercentageTotal = pipe(map(path(['splitPercentage'])), sum)

const isInvalidNumericInput = (v: unknown) => v == null || v === '' || Number.isNaN(Number(v))

const validationSchema = yup.object().shape({
  managementFee: yup.object().shape({
    type: yup.string().required('required.'),
    monthlyRentAmount: yup.number().required('required.'),
    /** @todo implement conditional validation i.e. netAmount|percentage  */
    // netAmount: yup.object().shape({
    //   value: yup.string().required('required.')
    //   // vatable: yup.boolean()
    // }),
    splits: yup
      .array()
      .of(
        yup.object().shape({
          agent: yup.object().shape({
            label: yup.string(),
            value: yup.string(),
          }),
          split: yup.number(),
        }),
      )
      .test('managementFee.splits', function (splits: $TSFixMe) {
        const commAmount = this.parent.netAmount?.value
        const commPercentage = this.parent.percentage?.value
        const splitPercentageTotal = getSplitPercentageTotal(splits)
        // @ts-expect-error
        if (splitPercentageTotal > 100) {
          return this.createError({
            path: this.path,
            message: "Commission splits can't exceed a total of 100%",
          })
        }
        // @ts-expect-error
        if (splitPercentageTotal < 100) {
          return this.createError({
            path: this.path,
            message: 'Commission splits must add up to 100%',
          })
        }
        const noPercentageSplits = filter((split: any) => !split.splitPercentage, splits)
        if (noPercentageSplits.length > 0) {
          return this.createError({
            path: this.path,
            message: 'All commission splits must have a value',
          })
        }
        const noAgentSplits = filter((split: any) => !split.agent.value || !split.agent.label, splits)
        if (noAgentSplits.length > 0) {
          return this.createError({
            path: this.path,
            message: 'All commission splits must have an agent',
          })
        }
        const agentIds = splits.map((split: $TSFixMe) => split.agent.value)
        const hasDuplicateAgents = new Set(agentIds).size !== agentIds.length
        if (hasDuplicateAgents) {
          return this.createError({
            path: this.path,
            message: 'Duplicate agents are not allowed',
          })
        }
        return true
      }),
    netAmount: yup.object().test('managementFee.netAmount', function (val) {
      const { monthlyRentAmount, paymentRulesTotal = 0, paymentRulesTotalExCommission = 0 } = this.parent
      const v = val?.value
      if (this.parent.type === commissionUndertakings.fixed && isInvalidNumericInput(v)) {
        return this.createError({
          path: this.path,
          message: 'must be a numerical value.',
        })
      }
      if (paymentRulesTotal > monthlyRentAmount) {
        return this.createError({
          path: this.path,
          message: `Commission cannot exceed ${formatCurrency(
            monthlyRentAmount - paymentRulesTotalExCommission,
          )}, the remaining funds after rental payments`,
        })
      }
      return true
    }),
    percentage: yup.object().test('managementFee.percentage', function (val) {
      const { monthlyRentAmount, paymentRulesTotal = 0, paymentRulesTotalExCommission = 0 } = this.parent
      const v = val?.value
      if (this.parent.type === commissionUndertakings.variable && isInvalidNumericInput(v)) {
        return this.createError({
          path: this.path,
          message: 'must be a numerical value.',
        })
      }
      if (paymentRulesTotal > monthlyRentAmount) {
        return this.createError({
          path: this.path,
          message: `Commission cannot exceed ${formatCurrency(
            monthlyRentAmount - paymentRulesTotalExCommission,
          )}, the remaining funds after rental payments`,
        })
      }
      return true
    }),
  }),
})

const CommissionForm = ({
  handleSubmit,
  initialValues,
  rentAmount,
  defaultResultSets,
  isDraftPortfolio,
  portfolioRentInvoice,
  isReadOnly,
  redirect,
  portfolioId,
}: TProps) => {
  return (
    <Formik
      validationSchema={validationSchema}
      enableReinitialize
      initialValues={initialValues}
      onSubmit={values => {
        handleSubmit(values)
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        submitCount,
        submitForm,
        dirty,
        isValid,
        isSubmitting,
      }) => (
        <div className={styles.root}>
          <RouterPromptModal
            when={dirty}
            isValid={isValid}
            isSubmitting={isSubmitting}
            onSave={submitForm}
            onDiscard={() => true}
          />
          <Form className={styles.form}>
            <fieldset disabled={isReadOnly}>
              <div className={styles.body}>
                <ManagementFeeForm
                  values={values.managementFee}
                  rentAmount={rentAmount}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  errors={errors}
                  touched={touched}
                  defaultResultSets={defaultResultSets}
                  submitCount={submitCount}
                  portfolioRentInvoice={portfolioRentInvoice}
                />
              </div>
              <div className={styles.footer}>
                {isDraftPortfolio ? (
                  <>
                    <Button onClick={() => redirect(`/leases/${portfolioId}/edit/rent-and-fees`)}>Back</Button>
                    {/* A zero commission is a valid choice, so advancing only requires a valid form. */}
                    <Button type="submit" disabled={Object.keys(errors).length > 0}>
                      Next
                    </Button>
                  </>
                ) : (
                  <Button type="submit" disabled={!dirty || Object.keys(errors).length > 0}>
                    Save
                  </Button>
                )}
              </div>
            </fieldset>
          </Form>
        </div>
      )}
    </Formik>
  )
}

export default CommissionForm
